<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Header from '@/components/landing/Header.vue';
import Footer from '@/components/landing/Footer.vue';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
import Hero from '@/components/landing/Hero.vue';
import CartIcon from '@/components/landing/icons/CartIcon.vue';
import TimeIcon from '@/components/landing/icons/TimeIcon.vue';
import SecureIcon from '@/components/landing/icons/SecureIcon.vue';
import PeopleIcon from '@/components/landing/icons/PeopleIcon.vue';
import { useAuthStore } from '@/stores/authStore';
import { storeToRefs } from 'pinia';
import { ROUTES } from '@/router/routeNames';
import api from '@/services/axios';
import { nairaSymbol } from '@/constants';
import { useRouter } from 'vue-router';
import mixpanel from '@/services/mixpanel';
const router = useRouter()
const auth = useAuthStore()
const { user } = storeToRefs(auth)
const popularDishes = ref([])
const popularBusinesses = ref([])


onMounted(async () => {
    mixpanel.track('Welcome Page Viewed');
    const { data } = await api.get('/api/welcome')
    popularBusinesses.value = data.businessesToShow;

    (data.businessesToShow as Array<Record<string, any>>).forEach((b) => popularDishes.value.push({...b.first_product, business: b}))
})

// const urlForBusiness = (businessId) => {
//     return user.value?.id ? `https://boxbuddy.me/dashboard/businesses/${businessId}` : `https://boxbuddy.me/businesses/${businessId}`
// }


const cards = ref([
    { icon: 'cart', title: 'Easy Ordering', description: 'Browse and order from a wide selection of fresh products with just a few clicks.'},
    { icon: 'time', title: 'Fast Delivery', description: 'Get anything delivered quickly and efficiently to your doorstep.'},
    { icon: 'secure', title: 'Secure Payments', description: 'Safe and secure payment processing for all your orders.'},
    { icon: 'support', title: 'Support Local', description: 'Help support local businesses in your community.'},
])

// const foodCategories = ref([
//   { name: 'Local Delicacies', color: 'bg-orange-500', description: 'Taste the authentic flavors of local cuisine', url: '085eb3e6-9d68-4312-8c28-9ebdaa5d6d87' },
//   { name: 'Fresh Produce', color: 'bg-green-500', description: 'Farm-fresh fruits and vegetables', url: '9749bedf-63ab-4bb7-b39a-0a1d294d1fca' },
//   { name: 'Baked Goods', color: 'bg-amber-500', description: 'Freshly baked bread and pastries', url: 'b6fed652-a29c-4d54-ba54-612f2ffd0091' },
//   { name: 'Healthy Options', color: 'bg-emerald-500', description: 'Nutritious meals for a balanced diet', url: '87e4640a-bb36-4f60-a24d-059cfb4d240b' },
//   { name: 'International Cuisine', color: 'bg-blue-500', description: 'Explore flavors from around the world', url: '463d682e-6e0e-4270-b68c-6a15e4d23f33' },
//   { name: 'Desserts & Treats', color: 'bg-pink-500', description: 'Indulge in sweet delights', url: 'b6fed652-a29c-4d54-ba54-612f2ffd0091' },
// ])



const iconToShow = (icon) => {
    switch (icon) {
        case 'cart':
            return CartIcon;
        case 'time':
            return TimeIcon;
        case 'secure':
            return SecureIcon;
        case 'support':
            return PeopleIcon;
    }
}

// function handleImageError() {
//     document.getElementById('screenshot-container')?.classList.add('!hidden');
//     document.getElementById('docs-card')?.classList.add('!row-span-1');
//     document.getElementById('docs-card-content')?.classList.add('!flex-row');
//     document.getElementById('background')?.classList.add('!hidden');
// }

// const heroRef = ref(null);
const featuresRef = ref(null);
const foodGalleryRef = ref(null);
const popularDishesRef = ref(null);
const ctaRef = ref(null);

const navigateTo = (url) => {
  return user.value ? router.push({ name: ROUTES.BUSINESS, params: { id: url } }) : router.push({ name: ROUTES.PUBLIC_BUSINESS, params: {id: url}})
}

</script>

<template>
    <div class="min-h-screen bg-white">
        <Header />

        <main class="pt-16 bg-gray-100 dark:bg-gray-900">
            <!-- Hero Section -->
            <Hero />
            <!-- Food Gallery Section -->
            <section ref="foodGalleryRef" class="py-20 bg-white">
                <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div class="mb-12 text-center">
                        <h2 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Explore Food Categories</h2>
                        <p class="mt-4 text-xl text-gray-600">Discover a wide variety of delicious options</p>
                    </div>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                        <div v-for="business in popularBusinesses" :key="business.id"
                             class="overflow-hidden bg-white rounded-lg shadow-lg food-card group">
                            <div class="flex overflow-hidden relative justify-center items-center h-60">

                                <img v-if="business.logo_url" :src="business.logo_url" alt="Business Logo">
                                <div class="text-2xl font-semibold" v-else-if="business.name.split(' ').length > 1">{{ business.name.split(' ')[0][0] + ' ' + business.name.split(' ')[1][0] }}</div>
                                <div class="text-2xl font-semibold" v-else>{{ business.name[0]}}</div>
                            </div>
                            <div class="p-6">
                                <h3 class="mb-2 text-xl font-semibold text-gray-900">{{ business.name }}</h3>
                                <p class="text-gray-600">{{ business.description ?? '' }}</p>
                                <PrimaryButton @click="mixpanel.track('Explore Business was clicked from Welcome Page', { business_name: business.name, business_uuid: business.uuid }); navigateTo(business.uuid)" class="px-4 py-2 mt-4 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
                                    Explore
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Popular Dishes Section -->
            <section ref="popularDishesRef" class="py-20 bg-gray-50">
                <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div class="mb-12 text-center">
                        <h2 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Popular Local Dishes</h2>
                        <p class="mt-4 text-xl text-gray-600">Taste the best of local cuisine</p>
                    </div>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                        <div v-for="dish in popularDishes" :key="dish.id"
                             class="overflow-hidden bg-white rounded-lg shadow-lg dish-card group">
                            <div class="overflow-hidden relative h-48">
                                <img :src="dish.image_url" alt="dish image" class="object-cover w-full h-full">
                            </div>
                            <div class="p-4">
                                <div class="flex justify-between items-center">
                                    <h3 class="text-lg font-semibold text-gray-900">{{ dish.name }}</h3>
                                    <span class="font-medium text-green-600">{{nairaSymbol}}{{ dish.price }}</span>
                                </div>
                                <PrimaryButton @click="mixpanel.track('Order Now was clicked from Welcome Page', { dish_name: dish.name, dish_uuid: dish.uuid, business_name: dish.business.name, business_uuid: dish.business.uuid }); navigateTo(dish.business.uuid)" class="px-4 py-2 mt-3 w-full text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
                                    Order Now
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Features Section -->
            <section ref="featuresRef"
                class="py-20 bg-gradient-to-b from-white to-green-50 dark:bg-gray-900">
                <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                        <div
                            v-for="card in cards"
                            :key="card.icon"
                            class="p-8 transition-all duration-300 ease-linear bg-white shadow-lg rounded-2xl feature-card hover:shadow-xl hover:translate-y-[-10px]">
                            <div
                                class="flex justify-center items-center mb-6 w-16 h-16 bg-green-100 rounded-2xl feature-icon">
                                <component :is="iconToShow(card.icon)" />
                            </div>
                            <h3 class="mb-3 text-xl font-semibold">{{card.title}}</h3>
                            <p class="text-gray-600">{{card.description}}</p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- CTA Section -->
            <section ref="ctaRef" class="overflow-hidden relative">
                <div class="absolute inset-0 bg-gradient-to-br from-green-600 to-green-800">
                    <div class="absolute inset-0 bg-black/60"></div>
                </div>
                <div class="relative z-20 py-20">
                    <div class="px-4 mx-auto max-w-7xl text-center sm:px-6 lg:px-8">
                        <div class="text-center cta-content">
                            <h2 class="mb-6 text-3xl font-black tracking-tight text-white drop-shadow-lg sm:text-4xl">
                                Join Our Community Today
                            </h2>
                            <p class="mx-auto mb-8 max-w-3xl text-xl font-medium text-white/90">
                                Experience the convenience of any item delivered to your doorstep.
                            </p>
                            <div class="flex flex-col gap-4 justify-center sm:flex-row">
                                <template v-if="!user">
                                    <PrimaryButton
                                        type="primary"
                                        as="link"
                                        :to="{name: ROUTES.REGISTER}"
                                        @click="mixpanel.track('Sign Up was clicked from Welcome Page')">
                                        Sign Up Now
                                    </PrimaryButton>
                                    <PrimaryButton
                                        type="secondary"
                                        as="link"
                                        :to="{name: ROUTES.LOGIN}"
                                        @click="mixpanel.track('Login was clicked from Welcome Page')">
                                        Login
                                    </PrimaryButton>
                                </template>
                                <template v-else>
                                    <PrimaryButton
                                        type="primary"
                                        as="link"
                                        :to="{name: ROUTES.DASHBOARD}"
                                        @click="mixpanel.track('Go to Dashboard was clicked')">
                                        Go to Dashboard
                                    </PrimaryButton>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>

        <Footer />
    </div>
</template>

<style scoped>
.feature-card {
    transition: all 0.3s ease;
}

.feature-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.feature-icon {
    transition: all 0.3s ease;
}

.feature-card:hover .feature-icon {
    transform: scale(1.1);
    background-color: #4ade80;
}

.feature-card:hover .feature-icon svg {
    color: white;
}
</style>

<style>
/* Food Gallery Animation */
.food-card {
    transition: all 0.3s ease;
}

.food-card:hover {
    transform: translateY(-5px);
}

.dish-card {
    transition: all 0.3s ease;
}

.dish-card:hover {
    transform: translateY(-5px);
}
</style>
